/**
 * LoginModal — in-page FOFUS ID login (DR-0006 amendment, 16 Oct).
 *
 * FOUNDER LAW: login on quote.fofus.in / designai.fofus.in must NOT navigate the
 * customer away from the page (full-page exits confuse and lose mid-flow state).
 * This modal hosts the SAME auth flows as the account.fofus.in /login page —
 * Google, password, WhatsApp-OTP / Email-OTP — rendered in an over-ley overlay.
 *
 * Sync law: on success the SAME context (useAuth) refreshes the profile
 * (fetchMe runs inside each auth action), the shared foid_session cookie has
 * already been set by the backend on .fofus.in, so the page stays put and every
 * logged-in surface (account chip, quote save, designer CTA) syncs instantly.
 *
 * Props:
 *   open        boolean — render when true
 *   onClose     () => void — called on backdrop/X/cancel/success
 *   onSuccess   () => void — optional extra hook (e.g. resume payment flow)
 */
import { useState, useEffect } from "react";
import { Sparkles, Mail, ArrowRight, AlertCircle, KeyRound, Loader2, MessageCircle, X } from "lucide-react";
import { useAuth } from "@/api/auth";
import apiClient from "@/api/client";
import GoogleSignIn from "@/components/GoogleSignIn";

type Tab = "password" | "otp";
type OtpMode = "whatsapp" | "email";

export default function LoginModal({ open, onClose, onSuccess, defaultTab = "password", subtitle }: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** GENERATION WALL LAW (17 Oct founder): walls opened mid-generation default
   *  to the MOBILE OTP tab — the personal account is keyed by mobile number. */
  defaultTab?: Tab;
  subtitle?: string;
}) {
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [otpMode, setOtpMode] = useState<OtpMode>("whatsapp");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(true);
  const { login, googleLogin, otpRequest, otpVerify } = useAuth();

  // Silent SSO first: a foid_session cookie from ANY fofus.in site logs the
  // customer in instantly — modal closes itself, page never changed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/auth/sso/session", { withCredentials: true });
        if (!cancelled && res.data?.valid) {
          // Mint local token for this host then close — profile syncs via context.
          await apiClient.post("/auth/sso/cookie", {}, { withCredentials: true }).catch(() => null);
          onSuccess?.(); onClose?.();
          return;
        }
      } catch { /* no session — keep form */ }
      if (!cancelled) setSsoChecking(false);
    })();
    return () => { cancelled = true; };
  }, [open, onClose, onSuccess]);

  // Esc-to-close + focus trap-lite
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const finish = () => { onSuccess?.(); onClose?.(); };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try { await login(email, password); finish(); }
    catch (err: unknown) { setError((err as { detail?: string }).detail || "Login failed"); }
    finally { setSubmitting(false); }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await otpRequest(otpMode === "whatsapp" ? { phone: phone.replace(/\D/g, "") } : { email });
      setOtpSent(true);
    } catch (err: unknown) { setError((err as { detail?: string }).detail || "Failed to send OTP"); }
    finally { setSubmitting(false); }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await otpVerify(otpMode === "whatsapp" ? { phone: phone.replace(/\D/g, "") } : { email }, otpCode);
      finish();
    } catch (err: unknown) { setError((err as { detail?: string }).detail || "OTP verification failed"); }
    finally { setSubmitting(false); }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setError(""); setSubmitting(true);
    try { await googleLogin(credential); finish(); }
    catch (err: unknown) { setError((err as { detail?: string }).detail || "Google login failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl relative">
        <button onClick={onClose} aria-label="Close login"
                className="absolute right-3 top-3 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6 text-center">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-hero shadow-glow">
            <Sparkles className="h-5 w-5 text-ink-900" strokeWidth={2.5} />
          </span>
          <h2 className="mt-3 text-lg font-bold text-ink-900">FOFUS ID</h2>
          <p className="mt-1 text-xs text-ink-500">{subtitle || "One account. All of FOFUS. You stay on this page."}</p>
        </div>

        {ssoChecking ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-primary-500" />
          </div>
        ) : (
          <>
            <div className="mb-5">
              <GoogleSignIn onSuccess={handleGoogleSuccess} onError={(e) => setError(e)} text="signin_with" />
            </div>

            <div className="mb-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-ink-200" />
              <span className="text-xs font-medium text-ink-600">OR</span>
              <div className="h-px flex-1 bg-ink-200" />
            </div>

            <div className="mb-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => { setTab("password"); setOtpSent(false); setError(""); }}
                className={`rounded-xl border py-2 text-sm font-medium transition-colors ${
                  tab === "password" ? "border-primary-400 bg-primary-50 text-primary-700" : "border-ink-300 text-ink-500 hover:bg-ink-100"}`}>
                Password
              </button>
              <button type="button" onClick={() => { setTab("otp"); setError(""); }}
                className={`rounded-xl border py-2 text-sm font-medium transition-colors ${
                  tab === "otp" ? "border-primary-400 bg-primary-50 text-primary-700" : "border-ink-300 text-ink-500 hover:bg-ink-100"}`}>
                WhatsApp / Email code
              </button>
            </div>

            {error && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
              </div>
            )}

            {tab === "password" && (
              <form className="space-y-4" onSubmit={handlePasswordLogin}>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="input-base" placeholder="you@example.com" required />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       className="input-base" placeholder="Password" required />
                <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
                  {submitting ? "Signing in..." : "Sign In"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}

            {tab === "otp" && !otpSent && (
              <form className="space-y-4" onSubmit={handleSendOTP}>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => { setOtpMode("whatsapp"); setError(""); }}
                    className={`flex items-center justify-center gap-1.5 rounded-xl border py-2 text-sm font-medium ${
                      otpMode === "whatsapp" ? "border-primary-400 bg-primary-50 text-primary-700" : "border-ink-300 text-ink-500 hover:bg-ink-100"}`}>
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </button>
                  <button type="button" onClick={() => { setOtpMode("email"); setError(""); }}
                    className={`flex items-center justify-center gap-1.5 rounded-xl border py-2 text-sm font-medium ${
                      otpMode === "email" ? "border-primary-400 bg-primary-50 text-primary-700" : "border-ink-300 text-ink-500 hover:bg-ink-100"}`}>
                    <Mail className="h-4 w-4" /> Email
                  </button>
                </div>
                {otpMode === "whatsapp" ? (
                  <div className="flex">
                    <span className="inline-flex items-center rounded-l-xl border border-r-0 border-ink-300 bg-ink-100 px-3 text-sm font-medium text-ink-700">+91</span>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                           className="input-base rounded-l-none pl-2" placeholder="98958 54640" maxLength={10} pattern="[0-9]{10}" required />
                  </div>
                ) : (
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                         className="input-base" placeholder="you@example.com" required />
                )}
                <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
                  {submitting ? "Sending..." : "Send Login Code"} <KeyRound className="h-4 w-4" />
                </button>
              </form>
            )}

            {tab === "otp" && otpSent && (
              <form className="space-y-4" onSubmit={handleVerifyOTP}>
                <div className="rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-700">
                  {otpMode === "whatsapp"
                    ? <>Code sent to <strong>+91 {phone}</strong> on WhatsApp.</>
                    : <>Code sent to <strong>{email}</strong>.</>}
                </div>
                <input type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       className="input-base text-center text-2xl tracking-[0.5em]" placeholder="000000" maxLength={6} required />
                <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
                  {submitting ? "Verifying..." : "Verify & Sign In"} <ArrowRight className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => { setOtpSent(false); setOtpCode(""); }}
                        className="w-full text-sm text-ink-500 hover:text-ink-700">
                  {otpMode === "whatsapp" ? "Use a different number" : "Use a different email"}
                </button>
              </form>
            )}

            <p className="mt-5 text-center text-xs text-ink-500">
              No account? It&apos;s created automatically when you first log in — nothing to sign up for.
            </p>
          </>
        )}
      </div>
    </div>
  );
}